
import { Component, Vue, Watch } from "vue-property-decorator";
import lodash from "lodash";
import { DimssaButton, ButtonState } from "@/components/shared/dimssa-button.vue";
import EarTag from "@/components/shared/EarTag.vue";
import KraalSelectInput from "@/components/KraalSelectInput.vue";
import AilmentSelectInput from "@/components/AilmentSelectInput.vue";
import NotesInput from "@/components/NotesInput.vue";
import { getAnimalListItems } from "@/helpers/animal-list-persist";
import { getTagColour, sgtinUiFriendlyTag } from "@/helpers/sgtin-ui-friendly-tag";
import { withdrawalDaysFromToday } from "@/helpers/withdrawal";
import { AnimalListItem } from "@/models/animal-list-item";
import * as Models from "@gigalot/data-models";

@Component({
  components: {
    DimssaButton,
    EarTag,
    KraalSelectInput,
    AilmentSelectInput,
    NotesInput,
  },
})
export default class Mortality extends Vue {
  mortality: Models.Mortality = this.createMortality();
  selectedAnimalListItem: any = "";
  selectedAnimalListItemDraft: any = "";
  selectedAnimal: Models.Animal | "" = "";
  withdrawalDays: number | "" = "";
  animalListItems: AnimalListItem[] = [];

  tagDialog: boolean = false;
  tagSearch: string = "";

  scanStatusMessage: string = "";
  scanButtonState: ButtonState = "ready";
  isUiLocked: boolean = false;

  changed: boolean = false;
  dateString: string = "";
  dateDialog: boolean = false;
  dateDraft: string = "";
  massDialog: boolean = false;
  massDraft: string = "";
  mass: string = "";
  costDialog: boolean = false;
  costDraft: string = "";
  veterinaryCost: string = "";
  ailments: Models.Ailment[] = [];
  selectedAilments: Models.Ailment[] = [];
  notes: string = "";

  uiKey: number = 0;
  selectedSourceKraalId: string = "";
  kraalIds: string[] = [];

  sgtinUiFriendlyTag = sgtinUiFriendlyTag;

  get tagColour() {
    return this.mortality?.sgtin ? getTagColour(this.mortality.sgtin as string)?.name : "green";
  }

  get owner() {
    if (!this.selectedAnimal || !this.selectedAnimal.customFeeder) return "";
    return this.selectedAnimal.customFeeder.name || this.selectedAnimal.customFeeder.companies?.[0]?.name || "";
  }

  get producer() {
    if (!this.selectedAnimal) {
      return "";
    }
    return this.selectedAnimal?.producer || "";
  }

  get formattedGender() {
    if (!this.selectedAnimal) {
      return "";
    }
    const gender = this.selectedAnimal?.gender || "";
    return gender ? gender[0].toUpperCase() + gender.slice(1).toLowerCase() : "";
  }

  get breed() {
    if (!this.selectedAnimal) {
      return "";
    }
    return this.selectedAnimal?.breed || "";
  }

  get moment() {
    return this.$store.state.moment;
  }

  rules = {
    validNumber: (value: string) => /^[0-9]*\.{0,1}[0-9]*$/.test(value) || "Invalid input.",
  };

  createMortality() {
    const ret = new Models.Mortality();
    ret.time = Date.now();
    ret.metadata = this.$store.getters["upload/getUpstreamMetadata"]();
    ret.app = "field";
    return ret;
  }

  get isTagDialogPersistent() {
    const hasSearch = this.tagSearch.trim().length > 0;
    return hasSearch || this.tagItemKey(this.selectedAnimalListItemDraft) !== this.tagItemKey(this.selectedAnimalListItem);
  }

  get isTagSearchReady() {
    return this.tagSearch.trim().length >= 3;
  }

  get filteredAnimalListItems() {
    const term = this.tagSearch.trim().toLowerCase();
    if (!this.isTagSearchReady) return [];
    return this.animalListItems.filter((a) => (a.sgtinUiFriendlyTag || "").toLowerCase().includes(term));
  }

  tagItemKey(item: any) {
    return item ? item.guid || item.sgtin || "" : "";
  }

  animalListItemLabel(item: AnimalListItem) {
    const gender = item.gender ? item.gender[0].toUpperCase() + item.gender.slice(1).toLowerCase() : "";
    const details = [item.breed, gender, item.owner].filter(Boolean).join(" - ");
    return details ? `${item.sgtinUiFriendlyTag} - ${details}` : `${item.sgtinUiFriendlyTag}`;
  }

  resetScanStatus() {
    this.scanButtonState = "ready";
    this.scanStatusMessage = "";
  }

  openTagDialog() {
    if (this.isUiLocked) {
      return;
    }

    this.selectedAnimalListItemDraft = this.selectedAnimalListItem || "";
    this.tagSearch = "";
    this.tagDialog = true;
  }

  openDateDialog() {
    if (this.isUiLocked) {
      return;
    }

    this.dateDraft = this.dateString || this.moment(this.mortality.time || Date.now()).format("YYYY-MM-DD");
    this.dateDialog = true;
  }

  saveDate() {
    this.dateString = this.dateDraft;
    this.mortality.time = this.moment(this.dateString).valueOf();
    this.changed = true;
    this.dateDialog = false;
  }

  clearDate() {
    this.dateDraft = "";
    this.dateString = "";
    this.mortality.time = undefined;
    this.changed = true;
  }

  openMassDialog() {
    if (this.isUiLocked) {
      return;
    }

    this.massDraft = this.mass || "";
    this.massDialog = true;
  }

  saveMass() {
    this.massChange(this.massDraft);
    this.changed = true;
    this.massDialog = false;
  }

  massChange(mass: any) {
    this.mass = mass ?? "";
    if (this.rules.validNumber(mass) !== true) {
      this.mortality.mass = undefined;
      return;
    }
    this.mortality.mass = isNaN(parseFloat(mass)) ? undefined : parseFloat(mass);
  }

  openCostDialog() {
    if (this.isUiLocked) {
      return;
    }

    this.costDraft = this.veterinaryCost || "";
    this.costDialog = true;
  }

  saveCost() {
    this.costChange(this.costDraft);
    this.changed = true;
    this.costDialog = false;
  }

  costChange(cost: any) {
    this.veterinaryCost = cost ?? "";
    if (this.rules.validNumber(cost) !== true) {
      this.mortality.cost = undefined;
      return;
    }
    this.mortality.cost = isNaN(parseFloat(cost)) ? undefined : parseFloat(cost);
  }

  selectTag(item: any) {
    this.selectedAnimalListItemDraft = item;
  }

  saveTagSelection() {
    this.selectedAnimalListItem = this.selectedAnimalListItemDraft;
    this.tagDialog = false;
    this.resetScanStatus();
  }

  clearTagSelection() {
    this.selectedAnimalListItemDraft = "";
    this.selectedAnimalListItem = "";
    this.tagSearch = "";
    this.resetScanStatus();
  }

  async onClickScanTag() {
    if (this.isUiLocked) {
      return;
    }

    try {
      this.scanButtonState = "busy";
      this.scanStatusMessage = "";
      const sgtin = await this.$store.dispatch("scan/singleScan");
      const animal = await this.$store.dispatch("data/getAnimal", sgtin);
      if (animal) {
        this.selectedAnimal = animal;
      } else {
        throw Error(`Animal not found for tag ${sgtin}.`);
      }
      this.scanButtonState = "success";
    } catch (error) {
      this.scanButtonState = "error";
      this.scanStatusMessage = `${error}`;
    }
  }

  focusTagSearchInput() {
    this.$nextTick(() =>
      requestAnimationFrame(() => {
        const input = this.$refs.tagSearchInput as Vue | { focus?: () => void } | undefined;
        if (input && typeof (input as any).focus === "function") {
          (input as any).focus();
        }
      })
    );
  }

  focusMassInput() {
    this.$nextTick(() =>
      requestAnimationFrame(() => {
        const input = this.$refs.massInput as Vue | { focus?: () => void } | undefined;
        if (input && typeof (input as any).focus === "function") {
          (input as any).focus();
        }
      })
    );
  }

  focusCostInput() {
    this.$nextTick(() =>
      requestAnimationFrame(() => {
        const input = this.$refs.costInput as Vue | { focus?: () => void } | undefined;
        if (input && typeof (input as any).focus === "function") {
          (input as any).focus();
        }
      })
    );
  }

  @Watch("tagDialog")
  onTagDialogChanged(isOpen: boolean) {
    if (isOpen) {
      this.focusTagSearchInput();
      return;
    }
    this.$nextTick(() => requestAnimationFrame(() => (document.activeElement as HTMLElement | null)?.blur()));
  }

  @Watch("dateDialog")
  onDateDialogChanged(isOpen: boolean) {
    if (isOpen) return;
    this.$nextTick(() => requestAnimationFrame(() => (document.activeElement as HTMLElement | null)?.blur()));
  }

  @Watch("massDialog")
  onMassDialogChanged(isOpen: boolean) {
    if (isOpen) {
      this.focusMassInput();
      return;
    }
    this.$nextTick(() => requestAnimationFrame(() => (document.activeElement as HTMLElement | null)?.blur()));
  }

  @Watch("costDialog")
  onCostDialogChanged(isOpen: boolean) {
    if (isOpen) {
      this.focusCostInput();
      return;
    }
    this.$nextTick(() => requestAnimationFrame(() => (document.activeElement as HTMLElement | null)?.blur()));
  }

  @Watch("selectedAnimal")
  selectedAnimalChanged(selectedAnimal: any) {
    const updateRequired = selectedAnimal?.sgtin !== this.mortality.sgtin;
    this.mortality.sgtin = selectedAnimal?.sgtin;

    if (updateRequired) {
      this.selectedAnimalListItem = this.mortality.sgtin ? this.animalListItems.find((a) => a.sgtin === this.mortality.sgtin) : "";
      if (!this.selectedAnimalListItem) this.selectedAnimalListItem = "";

      if (selectedAnimal) {
        this.withdrawalDays = withdrawalDaysFromToday(selectedAnimal) ?? "";
      } else {
        this.withdrawalDays = "";
      }

      this.changed = true;
    }

    this.uiKey++;
  }

  @Watch("selectedAnimalListItem")
  async onSelectedAnimalListItemChanged(selectedAnimalListItem: any) {
    if (selectedAnimalListItem) {
      try {
        this.selectedAnimal = await this.$store.dispatch("data/getAnimal", this.selectedAnimalListItem.sgtin);
      } catch (err: any) {
        console.error("error getting animal from server:", err);
      }
    } else {
      this.selectedAnimal = "";
      await this.$store.commit("scan/clearTags");
    }
  }

  @Watch("selectedSourceKraalId")
  onSelectedSourceKraalIdChanged(selectedSourceKraalId: string) {
    this.mortality.sourceKraalId = selectedSourceKraalId || undefined;
    this.changed = true;
  }

  @Watch("selectedAilments")
  onSelectedAilmentsChanged(selectedAilments: Models.Ailment[]) {
    this.mortality.ailments = selectedAilments || [];
    this.changed = true;
  }

  @Watch("notes")
  onNotesChanged(notes: string) {
    this.mortality.notes = notes ? [notes] : [];
    this.changed = true;
  }

  @Watch("changed")
  onChangeChanged(val: any) {
    this.$store.commit("navFuncs", { save: this.save, back: this.back });
  }

  async created() {
    this.$store.commit("navFuncs", {
      save: undefined,
      back: this.back,
    });

    this.animalListItems = await getAnimalListItems();
    this.kraalIds = await this.$store.dispatch("data/getKraalIds");
    this.ailments = await this.$store.dispatch("data/getAilments");
    this.selectedSourceKraalId = this.mortality?.sourceKraalId ?? "";
    this.dateString = this.mortality.time ? this.moment(this.mortality.time).format("YYYY-MM-DD") : "";
    this.massChange(this.mortality.mass);
    this.costChange(this.mortality.cost);
    this.selectedAilments = this.mortality.ailments || [];
    this.notes = this.mortality.notes?.length ? this.mortality.notes[0] : "";

    this.selectedAnimalListItem = this.mortality.sgtin ? this.animalListItems.find((a) => a.sgtin === this.mortality.sgtin) : "";
    if (!this.selectedAnimalListItem) this.selectedAnimalListItem = "";
  }

  save() {
    if (!this.mortality.sgtin) {
      this.$store.commit("popup/displayOk", "Can not save. No Tag ID selected.");
      return;
    }

    this.$store.commit("popup/displayYesNo", {
      message: "Are you sure that you want to save?",
      yesAction: async () => {
        try {
          this.isUiLocked = true;
          this.tagDialog = false;
          this.dateDialog = false;
          this.massDialog = false;
          this.costDialog = false;
          this.$store.commit("popup/displayWait", "Saving...");
          this.mortality.app = "field";
          const mortalityClone: Models.Mortality = lodash.cloneDeep(this.mortality);
          delete (mortalityClone as any).locationGuid;

          await this.$store.dispatch("data/addMortality", mortalityClone, { root: true });
          this.$store.commit("upload/numYetToUpload", "increment", { root: true });
          try {
            this.$store.dispatch("upload/upload", undefined, { root: true });
          } catch (e) {
            //silent failure is fine here, upload will likely fail here anyway if the user is still on the scanner's wifi
          }
          this.changed = false;
          this.$store.commit("navFuncs", { save: undefined, back: this.back });
          this.$store.commit("popup/hide");
        } catch (err: any) {
          this.isUiLocked = false;
          console.log(err);
          this.$store.commit("popup/hide");
          this.$store.commit("popup/displayOk", { message: `Error: ${err.message}` });
        }
      },
    });
  }

  back() {
    if (this.isUiLocked || !this.changed) {
      this.$router.go(-1);
      return;
    }

    this.$store.commit("popup/displayYesNo", {
      message: "Are you sure? Unsaved work will be lost.",
      yesAction: () => {
        this.mortality = this.createMortality();
        this.$router.go(-1);
      },
    });
  }

  isMassDraftNumber(): boolean {
    const value = Number(this.massDraft);
    return Number.isFinite(value);
  }

  isMassDraftPositive(): boolean {
    const value = Number(this.massDraft);
    return Number.isFinite(value) && value > 0;
  }

  isCostDraftNumber(): boolean {
    const value = Number(this.costDraft);
    return Number.isFinite(value);
  }

  isCostDraftPositive(): boolean {
    const value = Number(this.costDraft);
    return Number.isFinite(value) && value > 0;
  }
}
